import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";
import type { FunctionStats } from "@/lib/admin/hooks/useEdgeFunctionStats";

interface MailProcessorAlertProps {
  functions: FunctionStats[];
}

/** Allerta visibile solo quando mail-processor supera il 50% di errori. */
export function MailProcessorAlert({ functions }: MailProcessorAlertProps) {
  const { t } = useI18n();
  const mailStat = functions.find((s) => s.slug === "mail-processor");

  if (!mailStat || mailStat.total_calls === 0) return null;
  const errorRate = mailStat.error_calls / mailStat.total_calls;
  if (errorRate <= 0.5) return null;

  return (
    <Card className="border-red-200">
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-red-50 text-red-600 flex-shrink-0">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <CardTitle className="text-base font-semibold">
            {t("adminDashboard.mailProcessor.title")}
          </CardTitle>
          <span className="text-xs font-medium text-red-600 mt-0.5">
            {t("adminDashboard.mailProcessor.errorRateHigh")}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between py-2 px-3 rounded-md bg-red-50">
          <span className="text-sm font-medium text-red-700">
            {t("adminDashboard.mailProcessor.successCalls")}
          </span>
          <span className="text-sm font-semibold text-red-700">
            {mailStat.success_calls} / {mailStat.total_calls}{" "}
            {t("adminDashboard.mailProcessor.calls")}
          </span>
        </div>
        <div className="flex items-center justify-between py-2 px-3 rounded-md bg-red-50">
          <span className="text-sm font-medium text-red-700">
            {t("adminDashboard.mailProcessor.errorCalls")}
          </span>
          <span className="text-sm font-semibold text-red-700">
            {mailStat.error_calls} / {mailStat.total_calls}{" "}
            {t("adminDashboard.mailProcessor.calls")}
          </span>
        </div>
        <p className="text-sm text-gray-600 px-1">
          {t("adminDashboard.mailProcessor.description")}
        </p>
      </CardContent>
    </Card>
  );
}

export default MailProcessorAlert;
